import { useEffect, useRef, useState } from "react";

/**
 * Infinite-scroll wiring for a list that ends in a sentinel element.
 *
 * The sentinel node is kept in STATE rather than a ref on purpose: a ref does not
 * trigger a render, so an effect keyed on data/loading flags can run while the
 * sentinel is still absent from the DOM and then never re-run once it appears —
 * which is exactly how the observer stopped being attached in ContactsTable.
 * Holding the node in state makes its mount re-run the effect on its own.
 *
 * `rootRef` should be attached to the scrolling container so `rootMargin` measures
 * against it. Without a root the observer falls back to the viewport, where a
 * non-zero margin would prefetch against the wrong box, so it is dropped there.
 */
const useInfiniteScroll = ({
  onLoadMore,
  enabled = true,
  rootMargin = "200px",
  threshold = 0.1,
}) => {
  const [sentinelNode, setSentinelNode] = useState(null);
  const [rootNode, setRootNode] = useState(null);
  const inFlightRef = useRef(false);

  // One trigger must load one page. `enabled` is driven by the caller's loading
  // flag, which lands a render later than the callback, so without this latch a
  // click and an intersection in the same tick would both fire for one page.
  useEffect(() => {
    inFlightRef.current = false;
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !sentinelNode || !onLoadMore) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || inFlightRef.current) return;
        inFlightRef.current = true;
        onLoadMore();
      },
      { root: rootNode, rootMargin: rootNode ? rootMargin : "0px", threshold },
    );
    observer.observe(sentinelNode);

    return () => observer.disconnect();
  }, [enabled, sentinelNode, rootNode, onLoadMore, rootMargin, threshold]);

  const triggerLoadMore = () => {
    if (!enabled || !onLoadMore || inFlightRef.current) return;
    inFlightRef.current = true;
    onLoadMore();
  };

  return { sentinelRef: setSentinelNode, rootRef: setRootNode, triggerLoadMore };
};

export default useInfiniteScroll;
